import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ExternalLink, Loader2 } from 'lucide-react';

interface SimauService {
  id: number;
  name: string;
  description: string;
  icon: string;
  serviceUrl: string;
  order: number;
}

interface ApiResponse {
  success: boolean;
  data: SimauService[];
  total: number;
  message: string;
}

const BubbleHome: React.FC = () => {
  const [services, setServices] = useState<SimauService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBubbles, setShowBubbles] = useState(false);
  const [mainLogo, setMainLogo] = useState<SimauService | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [activeTooltip, setActiveTooltip] = useState<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    fetchServices();
  }, []);

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update document title and favicon when mainLogo is loaded
  useEffect(() => {
    if (mainLogo) {
      console.log('Updating meta tags for:', mainLogo.name);
      console.log('Icon URL:', mainLogo.icon);
      
      // Update document title
      document.title = mainLogo.name || 'SIMAU';
      
      // Update favicon - remove old and add new with cache bust
      if (mainLogo.icon) {
        // Remove all existing favicon links
        const existingLinks = document.querySelectorAll("link[rel*='icon']");
        console.log('Removing', existingLinks.length, 'existing favicon links');
        existingLinks.forEach(link => link.remove());
        
        // Use relative path if API_URL is empty (for nginx proxy)
        const apiUrl = process.env.REACT_APP_API_URL || '';
        let faviconUrl = mainLogo.icon;
        
        // If icon is relative path, prepend API URL
        if (faviconUrl.startsWith('/')) {
          faviconUrl = apiUrl + faviconUrl;
        }
        
        // Add cache bust parameter to force browser reload
        const cacheBust = `?v=${Date.now()}`;
        faviconUrl = faviconUrl + cacheBust;
        
        // Create new favicon link with shortcut icon
        const link = document.createElement('link');
        link.type = 'image/x-icon';
        link.rel = 'shortcut icon';
        link.href = faviconUrl;
        document.getElementsByTagName('head')[0].appendChild(link);
        
        // Also add as standard icon rel
        const iconLink = document.createElement('link');
        iconLink.rel = 'icon';
        iconLink.type = 'image/x-icon';
        iconLink.href = faviconUrl;
        document.getElementsByTagName('head')[0].appendChild(iconLink);
        
        // Add apple-touch-icon for iOS
        const appleIcon = document.createElement('link');
        appleIcon.rel = 'apple-touch-icon';
        appleIcon.href = faviconUrl;
        document.getElementsByTagName('head')[0].appendChild(appleIcon);
        
        console.log('Favicon updated to:', faviconUrl);
      }

      // Update meta description and SEO tags
      if (mainLogo.description) {
        // Meta description
        let metaDescription = document.querySelector('meta[name="description"]') as HTMLMetaElement;
        if (!metaDescription) {
          metaDescription = document.createElement('meta');
          metaDescription.name = 'description';
          document.getElementsByTagName('head')[0].appendChild(metaDescription);
        }
        metaDescription.content = mainLogo.description;

        // Open Graph meta tags for social media
        const ogTags = [
          { property: 'og:title', content: mainLogo.name },
          { property: 'og:description', content: mainLogo.description },
          { property: 'og:type', content: 'website' },
          { property: 'og:url', content: window.location.href },
        ];

        // Add og:image if icon exists
        if (mainLogo.icon) {
          const apiUrl = process.env.REACT_APP_API_URL || '';
          const imageUrl = mainLogo.icon.startsWith('/') ? apiUrl + mainLogo.icon : mainLogo.icon;
          ogTags.push({ property: 'og:image', content: imageUrl });
        }

        ogTags.forEach(tag => {
          let metaTag = document.querySelector(`meta[property="${tag.property}"]`) as HTMLMetaElement;
          if (!metaTag) {
            metaTag = document.createElement('meta');
            metaTag.setAttribute('property', tag.property);
            document.getElementsByTagName('head')[0].appendChild(metaTag);
          }
          metaTag.content = tag.content;
        });

        // Twitter Card meta tags
        const twitterTags = [
          { name: 'twitter:card', content: 'summary_large_image' },
          { name: 'twitter:title', content: mainLogo.name },
          { name: 'twitter:description', content: mainLogo.description },
        ];

        if (mainLogo.icon) {
          const apiUrl = process.env.REACT_APP_API_URL || '';
          const imageUrl = mainLogo.icon.startsWith('/') ? apiUrl + mainLogo.icon : mainLogo.icon;
          twitterTags.push({ name: 'twitter:image', content: imageUrl });
        }

        twitterTags.forEach(tag => {
          let metaTag = document.querySelector(`meta[name="${tag.name}"]`) as HTMLMetaElement;
          if (!metaTag) {
            metaTag = document.createElement('meta');
            metaTag.name = tag.name;
            document.getElementsByTagName('head')[0].appendChild(metaTag);
          }
          metaTag.content = tag.content;
        });

        // Keywords meta tag
        let metaKeywords = document.querySelector('meta[name="keywords"]') as HTMLMetaElement;
        if (!metaKeywords) {
          metaKeywords = document.createElement('meta');
          metaKeywords.name = 'keywords';
          document.getElementsByTagName('head')[0].appendChild(metaKeywords);
        }
        metaKeywords.content = `${mainLogo.name}, SIMAU, Sistem Informasi, Portal Layanan, BKPSDM`;

        console.log('SEO meta tags updated');
      }
    }
  }, [mainLogo]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      console.log('Canvas ref is null');
      return;
    }

    console.log('Canvas found, initializing animation...');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.log('Canvas context is null');
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let width: number, height: number, circles: any[], t = 0;

    function resize() {
      if (!canvas) return;
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
      console.log('Canvas resized:', width, 'x', height);
      circles = Array.from({ length: 70 }).map(() => ({
        x: Math.random() * width,
        y: Math.random() * height,
        dx: -0.6 + Math.random() * 1.2,
        dy: -0.6 + Math.random() * 1.2,
        color: `hsla(${Math.random() * 360},70%,60%,0.6)`  // Increased opacity from 0.35 to 0.6
      }));
    }

    function draw() {
      if (!ctx || !canvas) return;
      ctx.clearRect(0, 0, width, height);
      t += 0.005;

      // Draw circles with glow effect
      circles.forEach(c => {
        ctx.beginPath();
        ctx.fillStyle = c.color;
        // Add glow effect
        ctx.shadowBlur = 10;
        ctx.shadowColor = c.color;
        ctx.arc(c.x, c.y, 4, 0, Math.PI * 2);  // Increased size from 3 to 4
        ctx.fill();
        ctx.shadowBlur = 0;  // Reset shadow
        c.x += c.dx;
        c.y += c.dy;
        if (c.x < 0) c.x += width;
        if (c.x > width) c.x -= width;
        if (c.y < 0) c.y += height;
        if (c.y > height) c.y -= height;
      });

      // Text removed - using React components instead

      // Draw lines between close circles
      for (let i = 0; i < circles.length; i++) {
        for (let j = i + 1; j < circles.length; j++) {
          const a = circles[i];
          const b = circles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const distSq = dx * dx + dy * dy;
          const maxDist = 140;
          if (distSq < maxDist * maxDist) {
            const opacity = 1 - Math.sqrt(distSq) / maxDist;
            ctx.beginPath();
            ctx.strokeStyle = `rgba(255,255,255,${opacity * 0.6})`;  // Increased from 0.4 to 0.6
            ctx.lineWidth = 1.5;  // Increased from 1 to 1.5
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
      requestAnimationFrame(draw);
    }

    console.log('Setting up canvas animation...');
    window.addEventListener('resize', resize);
    resize();
    draw();
    console.log('Canvas animation started!');

    return () => {
      console.log('Cleaning up canvas animation');
      window.removeEventListener('resize', resize);
    };
  }, []);  // Changed from [showBubbles] to [] to run only once

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use relative path if API_URL is empty (for nginx proxy), otherwise use full URL
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const apiToken = process.env.REACT_APP_API_TOKEN;

      if (!apiToken) {
        throw new Error('API token is not configured');
      }

      console.log('Fetching from:', `${apiUrl}/api/public/simau/services`);
      console.log('Using token:', apiToken?.substring(0, 20) + '...');

      const response = await axios.get<ApiResponse>(`${apiUrl}/api/public/simau/services`, {
        headers: {
          'Authorization': `Bearer ${apiToken}`
        }
      });

      console.log('API Response:', response.data);

      if (response.data.success) {
        const allServices = response.data.data;
        
        // Find service with order = 0 for main logo
        const logoService = allServices.find((s: SimauService) => s.order === 0);
        setMainLogo(logoService || null);
        
        // Set other services (exclude order 0 from bubble menu) and sort by order
        const bubbleServices = allServices
          .filter((s: SimauService) => s.order !== 0)
          .sort((a: SimauService, b: SimauService) => a.order - b.order);
        setServices(bubbleServices);
      } else {
        throw new Error(response.data.message || 'Failed to fetch services');
      }
    } catch (err: any) {
      console.error('Error fetching SIMAU services:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleServiceClick = (serviceUrl: string, serviceId: number) => {
    // For mobile: first tap shows tooltip, second tap opens service
    if (isMobile) {
      if (activeTooltip === serviceId) {
        // Second tap - open service
        window.open(serviceUrl, '_blank', 'noopener,noreferrer');
        setActiveTooltip(null);
      } else {
        // First tap - show tooltip
        setActiveTooltip(serviceId);
      }
    } else {
      // Desktop - direct open
      window.open(serviceUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleLogoClick = () => {
    setShowBubbles(!showBubbles);
    setActiveTooltip(null); // Reset tooltip when toggling bubbles
  };

  // Calculate bubble positions based on number of services
  const getBubblePosition = (index: number, total: number): { style: React.CSSProperties; tooltipPosition: 'top' | 'bottom' | 'left' | 'right' } => {
    // Use state isMobile for responsive behavior
    const spacing = isMobile ? 16 : 24;
    const translateY = isMobile ? 12 : 12;
    
    const positions = [
      { style: { position: 'absolute' as const, left: '50%', bottom: '100%', transform: `translate(-50%, -${translateY}px)` }, tooltipPosition: 'top' as const },
      { style: { position: 'absolute' as const, left: `calc(100% + ${spacing}px)`, top: '50%', transform: 'translateY(-50%)' }, tooltipPosition: 'right' as const },
      { style: { position: 'absolute' as const, right: `calc(100% + ${spacing}px)`, top: '50%', transform: 'translateY(-50%)' }, tooltipPosition: 'left' as const },
      { style: { position: 'absolute' as const, left: '50%', top: `calc(100% + ${spacing}px)`, transform: 'translateX(-50%)' }, tooltipPosition: 'bottom' as const },
    ];

    if (total <= 4) {
      return positions[index];
    }

    // For more than 4 services, distribute evenly in a circle
    const angle = (index / total) * 2 * Math.PI - Math.PI / 2;
    
    // Back to original radius since we're using tooltips
    const baseRadius = isMobile ? 120 : 200;
    const radiusIncrement = Math.max(0, (total - 5) * (isMobile ? 8 : 10));
    const radius = baseRadius + radiusIncrement;
    
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;
    
    // Determine tooltip position based on bubble location
    // Different logic for mobile vs desktop
    let tooltipPosition: 'top' | 'bottom' | 'left' | 'right' = 'bottom';
    
    const threshold = radius * 0.3; // 30% threshold
    const absX = Math.abs(x);
    const absY = Math.abs(y);
    
    // For mobile: prioritize vertical (top/bottom) to avoid horizontal overflow
    // For desktop: prioritize horizontal (left/right) for better visibility
    if (isMobile) {
      // Mobile: vertical positioning
      if (y < -threshold) {
        tooltipPosition = 'top';
      } else if (y > threshold) {
        tooltipPosition = 'bottom';
      } else {
        tooltipPosition = x > 0 ? 'right' : 'left';
      }
    } else {
      // Desktop: horizontal positioning preferred
      if (absX > absY) {
        // More horizontal - use left/right
        tooltipPosition = x > 0 ? 'right' : 'left';
      } else {
        // More vertical - still use left/right for desktop safety
        tooltipPosition = x >= 0 ? 'right' : 'left';
      }
    }
    
    return {
      style: {
        position: 'absolute' as const,
        left: '50%',
        top: '50%',
        transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`
      },
      tooltipPosition
    };
  };

  const getBubbleColor = (index: number) => {
    const colors = [
      'rgba(59, 130, 246, 0.8)', // Blue
      'rgba(22, 163, 74, 0.8)', // Green
      'rgba(5, 150, 105, 0.8)', // Teal
      'rgba(147, 51, 234, 0.8)', // Purple
      'rgba(234, 88, 12, 0.8)', // Orange
      'rgba(219, 39, 119, 0.8)', // Pink
    ];
    return colors[index % colors.length];
  };

  if (loading) {
    return (
      <div className="relative bg-gray-900 min-h-screen flex items-center justify-center">
        <canvas ref={canvasRef} className="fixed inset-0 -z-10" />
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-white" />
          <p className="text-white text-lg">Loading SIMAU Services...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative bg-gray-900 min-h-screen flex items-center justify-center">
        <canvas ref={canvasRef} className="fixed inset-0 -z-10" />
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-red-500/20 border border-red-500 rounded-lg p-6 backdrop-blur-sm">
            <h3 className="text-red-200 font-semibold mb-2 text-xl">Error Loading Services</h3>
            <p className="text-red-100 text-sm mb-4">{error}</p>
            <button
              onClick={fetchServices}
              className="bg-red-600 text-white px-6 py-3 rounded-md hover:bg-red-700 transition-colors font-semibold"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative bg-gray-900 min-h-screen flex flex-col">
      {/* Animated Futuristic Background Canvas */}
      <canvas 
        ref={canvasRef} 
        className="fixed inset-0" 
        style={{ 
          zIndex: 0,
          pointerEvents: 'none'
        }} 
      />

      {/* Main Content */}
      <main className="flex-grow flex items-center justify-center text-center px-4">
        <div className="relative w-full max-w-6xl mx-auto flex flex-col items-center justify-center" style={{ minHeight: showBubbles ? '600px' : '100vh', paddingTop: showBubbles ? '0' : '0', paddingBottom: showBubbles ? '0' : '0' }}>
          {/* Main Logo Bubble - Centered */}
          <div
            onClick={handleLogoClick}
            className="main-logo mx-auto relative transition-all duration-500 hover:scale-105 cursor-pointer z-20"
          >
            {mainLogo && mainLogo.icon ? (
              // Logo with bubble effects
              <div className="relative w-full h-full rounded-full bubble-shimmer bubble-glow overflow-hidden"
                style={{
                  background: 'radial-gradient(ellipse at 30% 30%, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 30%, rgba(59, 130, 246, 0.8) 60%, rgba(37, 99, 235, 0.9) 100%)',
                  boxShadow: '0 0 20px rgba(59, 130, 246, 0.5), 0 0 40px rgba(59, 130, 246, 0.3), 0 0 60px rgba(59, 130, 246, 0.1), inset 0 0 20px rgba(255, 255, 255, 0.1)'
                }}
              >
                {/* Bubble highlights */}
                <div className="absolute top-8 left-12 w-16 h-16 bg-white/40 rounded-full blur-sm"></div>
                <div className="absolute top-12 left-16 w-8 h-8 bg-white/60 rounded-full blur-xs"></div>
                <div className="absolute bottom-16 right-12 w-6 h-6 bg-white/30 rounded-full"></div>
                <div className="absolute bottom-20 right-16 w-4 h-4 bg-white/50 rounded-full"></div>
                
                {/* Logo on top of bubble */}
                <img 
                  src={mainLogo.icon.startsWith('data:') ? mainLogo.icon : `${process.env.REACT_APP_API_URL || ''}${mainLogo.icon}`}
                  alt={mainLogo.name}
                  className="absolute inset-0 w-full h-full object-contain p-4 z-10"
                  onError={(e) => {
                    console.error('Failed to load main logo:', mainLogo.icon);
                    // Hide image and show fallback bubble
                    const img = e.target as HTMLImageElement;
                    const parent = img.parentElement;
                    if (parent) parent.style.display = 'none';
                    const fallback = parent?.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
              </div>
            ) : null}
            
            {/* Fallback bubble (shown if no logo or image fails) */}
            <div 
              className="bubble-shimmer bubble-glow rounded-full flex items-center justify-center"
              style={{ 
                display: mainLogo && mainLogo.icon ? 'none' : 'flex',
                width: '100%',
                height: '100%',
                background: 'radial-gradient(ellipse at 30% 30%, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 30%, rgba(59, 130, 246, 0.8) 60%, rgba(37, 99, 235, 0.9) 100%)',
                boxShadow: '0 0 20px rgba(59, 130, 246, 0.5), 0 0 40px rgba(59, 130, 246, 0.3), 0 0 60px rgba(59, 130, 246, 0.1), inset 0 0 20px rgba(255, 255, 255, 0.1)'
              }}
            >
              {/* Bubble highlights */}
              <div className="absolute top-8 left-12 w-16 h-16 bg-white/40 rounded-full blur-sm"></div>
              <div className="absolute top-12 left-16 w-8 h-8 bg-white/60 rounded-full blur-xs"></div>
              <div className="absolute bottom-16 right-12 w-6 h-6 bg-white/30 rounded-full"></div>
              <div className="absolute bottom-20 right-16 w-4 h-4 bg-white/50 rounded-full"></div>

              {/* Text content */}
              <div className="relative z-10 text-center">
                <div className="text-5xl font-bold text-white tracking-wider">SIMAU</div>
                <div className="w-16 h-0.5 bg-white/60 mx-auto mt-2"></div>
                <div className="text-xs text-white/90 mt-1">PORTAL</div>
              </div>
            </div>
          </div>

          {/* Title and Description - Fades when bubbles shown */}
          {mainLogo && !showBubbles && (
            <div 
              className="mt-8 text-center px-4 max-w-2xl mx-auto transition-all duration-500 ease-in-out"
            >
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 tracking-wide">
                {mainLogo.name}
              </h1>
              {mainLogo.description && (
                <p className="text-base md:text-lg text-gray-300 leading-relaxed">
                  {mainLogo.description}
                </p>
              )}
            </div>
          )}

          {/* Service Bubbles */}
          {showBubbles && services.map((service, index) => {
            const position = getBubblePosition(index, services.length);
            const color = getBubbleColor(index);
            const isTooltipActive = activeTooltip === service.id;

            // Get tooltip position style - smart positioning to avoid viewport edges and logo
            const getTooltipStyle = (): React.CSSProperties => {
              const baseStyle: React.CSSProperties = {
                position: 'absolute',
                zIndex: 1000,
                pointerEvents: 'none',
                whiteSpace: 'normal',
                maxWidth: isMobile ? '180px' : '220px',
              };

              // Mobile: use vertical tooltips based on bubble position
              // Desktop: use horizontal tooltips (left/right) for better visibility
              if (isMobile) {
                // For mobile, check if bubble is in upper or lower half
                // If tooltipPosition is 'top' or bubble is in upper area, show tooltip above
                if (position.tooltipPosition === 'top') {
                  return { 
                    ...baseStyle, 
                    bottom: '100%', 
                    left: '50%', 
                    transform: 'translateX(-50%)', 
                    marginBottom: '12px' 
                  };
                } else {
                  // For other positions, show tooltip below
                  return { 
                    ...baseStyle, 
                    top: '100%', 
                    left: '50%', 
                    transform: 'translateX(-50%)', 
                    marginTop: '12px' 
                  };
                }
              } else {
                // Desktop: Smart positioning - tooltip always goes AWAY from center/logo
                switch (position.tooltipPosition) {
                  case 'top':
                    return { ...baseStyle, left: '100%', top: '50%', transform: 'translateY(-50%)', marginLeft: '12px' };
                  case 'bottom':
                    return { ...baseStyle, left: '100%', top: '50%', transform: 'translateY(-50%)', marginLeft: '12px' };
                  case 'left':
                    return { ...baseStyle, right: '100%', top: '50%', transform: 'translateY(-50%)', marginRight: '12px' };
                  case 'right':
                    return { ...baseStyle, left: '100%', top: '50%', transform: 'translateY(-50%)', marginLeft: '12px' };
                  default:
                    return { ...baseStyle, left: '100%', top: '50%', transform: 'translateY(-50%)', marginLeft: '12px' };
                }
              }
            };

            return (
              <div
                key={service.id}
                className="transition-all duration-300 group"
                style={position.style}
              >
                <button
                  onClick={() => handleServiceClick(service.serviceUrl, service.id)}
                  className="bubble bubble-shimmer flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer relative"
                  style={{
                    width: isMobile ? '90px' : '120px',
                    height: isMobile ? '90px' : '120px',
                    borderRadius: '50%',
                    background: `radial-gradient(ellipse at 30% 30%, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 30%, ${color} 60%, ${color.replace('0.8', '0.9')} 100%)`,
                    boxShadow: `0 0 20px ${color.replace('0.8', '0.5')}, 0 0 40px ${color.replace('0.8', '0.3')}, 0 0 60px ${color.replace('0.8', '0.1')}, inset 0 0 20px rgba(255, 255, 255, 0.1)`,
                    animation: 'float 3s ease-in-out infinite',
                    animationDelay: `${index * 0.5}s`
                  }}
                >
                  {/* Bubble highlights */}
                  <div className="absolute top-3 left-6 w-8 h-8 bg-white/40 rounded-full blur-sm"></div>
                  <div className="absolute top-5 left-8 w-4 h-4 bg-white/60 rounded-full blur-xs"></div>
                  <div className="absolute bottom-8 right-6 w-3 h-3 bg-white/30 rounded-full"></div>
                  <div className="absolute bottom-10 right-8 w-2 h-2 bg-white/50 rounded-full"></div>

                  {/* Content */}
                  <div className="relative z-10 text-center px-2">
                    {service.icon ? (
                      <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mb-2 mx-auto">
                        <img 
                          src={service.icon.startsWith('data:') ? service.icon : `${process.env.REACT_APP_API_URL || ''}${service.icon}`}
                          alt={service.name} 
                          className="w-6 h-6 object-contain"
                          onError={(e) => {
                            console.error('Failed to load service icon:', service.name, service.icon);
                            const img = e.target as HTMLImageElement;
                            img.style.display = 'none';
                            const parent = img.parentElement;
                            if (parent) {
                              parent.innerHTML = '<svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path></svg>';
                            }
                          }}
                        />
                      </div>
                    ) : (
                      <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mb-2 mx-auto">
                        <ExternalLink className="w-6 h-6 text-white" />
                      </div>
                    )}
                    <div className="text-white text-xs font-bold leading-tight">
                      <div className="line-clamp-2">{service.name}</div>
                    </div>
                  </div>

                  {/* Tooltip - shown on hover (desktop) or tap (mobile) */}
                  {service.description && (
                    <div 
                      className={`tooltip-content ${
                        isMobile 
                          ? (isTooltipActive ? 'opacity-100 visible' : 'opacity-0 invisible')
                          : 'opacity-0 invisible group-hover:opacity-100 group-hover:visible'
                      } transition-all duration-300`}
                      style={getTooltipStyle()}
                    >
                      <div className="backdrop-blur-md bg-gray-900/95 text-white text-xs leading-relaxed rounded-lg px-4 py-3 shadow-2xl border border-white/20">
                        <div className="font-semibold mb-1">{service.name}</div>
                        <div className="text-gray-300">{service.description}</div>
                        {isMobile && isTooltipActive && (
                          <div className="text-blue-300 text-[10px] mt-2 italic">Tap lagi untuk membuka</div>
                        )}
                      </div>
                    </div>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </main>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          25% { transform: translateY(-10px) rotate(1deg); }
          50% { transform: translateY(-5px) rotate(0deg); }
          75% { transform: translateY(-15px) rotate(-1deg); }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Shimmer effect - DISABLED */
        /*
        .bubble-shimmer::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.3) 50%, transparent 70%);
          animation: shimmer 4s linear infinite;
          pointer-events: none;
          z-index: 5;
        }

        @keyframes shimmer {
          0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
          100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
        }
        */

        .main-logo {
          width: 160px;
          height: 160px;
        }

        @media (min-width: 768px) {
          .main-logo {
            width: 220px !important;
            height: 220px !important;
          }
        }

        @media (min-width: 1024px) {
          .main-logo {
            width: 240px !important;
            height: 240px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default BubbleHome;
